import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { addCheck } from '../../services/ApiService';
import { AddCheckRequest } from '../../models/SafetyPortalTypes';
import { FormErrorHandler } from '../../utils/FormErrorHandler';
import { ErrorTypeHandler } from '../../utils/ErrorTypeHandler';
import { ApiError } from '../../models/ApiError';
import CameraUpload from '../../components/CameraUpload/CameraUpload';
import { generateClientFileId } from '../../utils/fileUtils';
import styles from './AddCheck.module.css';

interface AddCheckProps {
  itemId: string;
  onCheckAdded?: () => void;
}

const AddCheck: React.FC<AddCheckProps> = ({ itemId, onCheckAdded }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<AddCheckRequest>({
    date: new Date().toISOString().split('T')[0],
    notes: '',
    passed: true,
  });
  const [files, setFiles] = useState<File[]>([]);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [generalError, setGeneralError] = useState<string>('');

  const addCheckMutation = useMutation({
    mutationFn: async (data: AddCheckRequest) => {
      const result = await addCheck(itemId, data);
      
      // Upload files if any - match by clientFileId
      if (files.length > 0 && result.check.files) {
        // Get the file metadata that was sent in the request
        const fileMetadata: Array<{
          fileName: string;
          fileExtension: string;
          contentType: string;
          clientFileId?: string;
        }> = data.files || [];
        
        const uploadPromises = files.map(async (file, index) => {
          // Find the request metadata for this file using the same index
          const requestFile = fileMetadata[index];
          if (!requestFile || !requestFile.clientFileId) return { success: true, fileName: file.name };
          
          // Find the response file with matching clientFileId
          const responseFile = result.check.files.find(
            f => f.clientFileId === requestFile.clientFileId
          );
          
          if (responseFile?.presignUrl) {
            try {
              const response = await fetch(responseFile.presignUrl, {
                method: 'PUT',
                headers: {
                  'Content-Type': responseFile.contentType,
                },
                body: file,
              });
              
              if (!response.ok) {
                throw new Error(`Upload failed with status ${response.status}`);
              }
              
              return { success: true, fileName: file.name };
            } catch (error) {
              return { 
                success: false, 
                fileName: file.name, 
                error: error instanceof Error ? error.message : 'Unknown error' 
              };
            }
          }
          
          return { success: true, fileName: file.name };
        });
        
        const results = await Promise.allSettled(uploadPromises);
        const failedUploads: string[] = [];
        
        results.forEach((result, index) => {
          if (result.status === 'fulfilled' && result.value && !result.value.success) {
            failedUploads.push(result.value.fileName);
          } else if (result.status === 'rejected') {
            failedUploads.push(files[index]?.name || `File ${index + 1}`);
          }
        });
        
        if (failedUploads.length > 0) {
          const errorMessage = failedUploads.length === 1
            ? t('Checks.Messages.FileUploadError', { fileName: failedUploads[0] }) || `Failed to upload file: ${failedUploads[0]}`
            : t('Checks.Messages.MultipleFileUploadError', { count: failedUploads.length }) || `Failed to upload ${failedUploads.length} file(s): ${failedUploads.join(', ')}`;
          throw new Error(errorMessage);
        }
      }
      
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['item', itemId] });
      
      // Reset form
      setFormData({
        date: new Date().toISOString().split('T')[0],
        notes: '',
        passed: true,
      });
      setFiles([]);
      setFormErrors({});
      setGeneralError('');
      
      onCheckAdded?.();
    },
    onError: (error: any) => {
      handleError(error);
    },
  });

  const handleError = (error: any) => {
    if (ApiError.isApiError(error)) {
      const fieldErrors = FormErrorHandler.extractFieldErrors(error);
      if (Object.keys(fieldErrors).length > 0) {
        setFormErrors(fieldErrors);
      } else {
        const errorMessage = ErrorTypeHandler.getUserFriendlyMessage(error);
        setGeneralError(errorMessage);
      }
    } else if (error instanceof Error && error.message) {
      // Handle regular Error objects (e.g., file upload errors)
      setGeneralError(error.message);
    } else {
      setGeneralError(t('Checks.Messages.AddError'));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: '' }));
    setGeneralError('');
  };

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value === 'true' }));
    setFormErrors((prev) => ({ ...prev, [name]: '' }));
    setGeneralError('');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(Array.from(e.target.files));
    }
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormErrors({});
    setGeneralError('');

    // Validation
    const errors: Record<string, string> = {};
    if (!formData.date) {
      errors.date = t('Checks.Errors.DateRequired');
    }
    if (formData.passed === undefined) {
      errors.passed = t('Checks.Errors.PassedRequired');
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    // Prepare file metadata with clientFileId
    const fileMetadata = files.map((file) => {
      const extension = file.name.split('.').pop() || '';
      return {
        fileName: file.name.replace(/\.[^/.]+$/, ''),
        fileExtension: extension,
        contentType: file.type || 'application/octet-stream',
        clientFileId: generateClientFileId(),
      };
    });

    const checkData: AddCheckRequest = {
      date: new Date(formData.date).toISOString(),
      notes: formData.notes?.trim() || undefined,
      passed: formData.passed,
      files: fileMetadata.length > 0 ? fileMetadata : undefined,
    };

    addCheckMutation.mutate(checkData);
  };

  return (
    <Box className={styles.container}>
      <Typography variant="h6" gutterBottom>
        {t('Checks.AddTitle')}
      </Typography>

      <form onSubmit={handleSubmit}>
        {generalError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {generalError}
          </Alert>
        )}

        <TextField
          fullWidth
          margin="normal"
          name="date"
          label={t('Checks.Form.Date')}
          type="date"
          value={formData.date}
          onChange={handleInputChange}
          InputLabelProps={{ shrink: true }}
          error={!!formErrors.date}
          helperText={formErrors.date}
          required
        />

        <FormControl fullWidth margin="normal" error={!!formErrors.passed}>
          <InputLabel id="passed-label">{t('Checks.Form.Passed')}</InputLabel>
          <Select
            name="passed"
            value={formData.passed ? 'true' : 'false'}
            onChange={handleSelectChange}
            labelId="passed-label"
            label={t('Checks.Form.Passed')}
          >
            <MenuItem value="true">{t('Checks.Passed.Yes')}</MenuItem>
            <MenuItem value="false">{t('Checks.Passed.No')}</MenuItem>
          </Select>
          {formErrors.passed && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
              {formErrors.passed}
            </Typography>
          )}
        </FormControl>

        <TextField
          fullWidth
          margin="normal"
          name="notes"
          label={t('Checks.Form.Notes')}
          value={formData.notes}
          onChange={handleInputChange}
          error={!!formErrors.notes}
          helperText={formErrors.notes}
          multiline
          rows={3}
        />

        <Box mt={2} mb={2}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            {t('Checks.Form.Images')}
          </Typography>
          <CameraUpload onFilesSelected={setFiles} selectedFiles={files} />
        </Box>

        <Box mt={3} display="flex" gap={2}>
          <Button
            type="submit"
            variant="contained"
            disabled={addCheckMutation.isPending}
          >
            {addCheckMutation.isPending ? t('Checks.Adding') : t('Checks.Add')}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default AddCheck;

